import { Colors, Icon } from '@amelio/ui-library';

import { AlreadyHeldMention } from './AlreadyHeldMention';
import { GREYED_ROW_SURFACE } from './CompetencyRow';

import type { Competency, CompetencyGroup } from '@/types/competency/Competency';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getTextByLanguage } from '@/utils/localization';

type AddedByGroupItem = {
    group: CompetencyGroup;
    items: Competency[];
};

type AddedCompetenciesSectionProps = {
    addedByGroup: AddedByGroupItem[];
    totalCount: number;
    /** The competencies the employee already holds in their current profile, shown greyed. */
    heldIds?: ReadonlySet<string>;
    onRemoveCompetency: (competencyId: string) => void;
};

export const AddedCompetenciesSection = ({
    addedByGroup,
    totalCount,
    heldIds,
    onRemoveCompetency,
}: AddedCompetenciesSectionProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    if (addedByGroup.length === 0) {
        return null;
    }

    return (
        <div className={'mt-2 border-t border-[#e2e8f0] pt-6'}>
            <div className={'mb-4 flex items-center gap-2'}>
                <Icon
                    name={'essential-add'}
                    size={'lg'}
                    color={Colors.PRIMARY_500}
                />

                <p className={'text-body text-navy font-semibold'}>
                    {t('competencies.competencyProfilesAdmin.selector.addedTitle')}
                </p>

                <span
                    className={
                        'bg-primary/10 text-primary text-caption rounded-full px-2 py-0.5 font-medium'
                    }
                >
                    {t('competencies.competencyProfilesAdmin.selector.addedCount', {
                        count: totalCount,
                    })}
                </span>
            </div>

            <div className={'flex flex-col gap-4'}>
                {addedByGroup.map(({ group, items }) => (
                    <div key={group.id}>
                        <div className={'mb-2 flex items-center gap-2 px-1'}>
                            <p className={'text-navy/80 text-body font-semibold'}>
                                {getTextByLanguage(group.name, language)}
                            </p>

                            <span
                                className={
                                    'bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[11px] font-medium'
                                }
                            >
                                {items.length}
                            </span>
                        </div>

                        <div className={'flex flex-col gap-2'}>
                            {items.map((competency) => {
                                const isHeld = heldIds?.has(competency.id) ?? false;

                                return (
                                    <div
                                        key={competency.id}
                                        className={
                                            'group ds-rounded-sm flex items-center justify-between gap-3 border px-4 py-3 ' +
                                            (isHeld
                                                ? GREYED_ROW_SURFACE
                                                : 'bg-primary/5 border-primary/30')
                                        }
                                        data-testid={`roster-selector-added-${competency.id}`}
                                    >
                                        <div
                                            className={
                                                'flex min-w-0 flex-1 flex-wrap items-center gap-2'
                                            }
                                        >
                                            <p className={'text-navy text-body font-medium'}>
                                                {getTextByLanguage(competency.name, language)}
                                            </p>

                                            {isHeld && <AlreadyHeldMention />}
                                        </div>

                                        <button
                                            type={'button'}
                                            onClick={() => onRemoveCompetency(competency.id)}
                                            className={
                                                'text-error ml-4 shrink-0 text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100'
                                            }
                                            data-testid={`roster-selector-added-remove-${competency.id}`}
                                        >
                                            {t(
                                                'competencies.competencyProfilesAdmin.selector.remove',
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
